import { X } from 'lucide-react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { useT } from '../lib/i18n'
import { useOverlayRoot } from './OverlayContext'

interface ToastMessage {
  id: number
  text: string
  tone: 'neutral' | 'good' | 'over'
}

interface ToastApi {
  show: (text: string, tone?: ToastMessage['tone']) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const AUTO_DISMISS = 4000

/**
 * Toasts: one at a time, above the tab bar, inside the phone column.
 *
 * **One at a time on purpose.** A stack of toasts is a queue of things the user
 * did not read; the second one is already an admission that the first was
 * ignored. A new message replaces the current one, so what is on screen is
 * always the most recent thing that happened.
 *
 * The auto-dismiss timer pauses while a pointer is down on the toast — the
 * gesture of "wait, let me read that" is holding it, and a message that
 * vanishes under a finger is worse than no message.
 *
 * `role="status"` + `aria-live="polite"`: a toast is a confirmation, not an
 * interruption. Anything that must be acknowledged is a `Sheet`, not this.
 *
 * SUAP-0103.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useT()
  const [message, setMessage] = useState<ToastMessage | null>(null)
  const nextId = useRef(1)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const overlayRoot = useOverlayRoot()

  const clear = useCallback(() => {
    clearTimeout(timer.current)
    timer.current = undefined
  }, [])

  const schedule = useCallback(() => {
    clear()
    timer.current = setTimeout(() => setMessage(null), AUTO_DISMISS)
  }, [clear])

  const api = useMemo<ToastApi>(
    () => ({
      show: (text, tone = 'neutral') => {
        setMessage({ id: nextId.current++, text, tone })
      },
    }),
    [],
  )

  useEffect(() => {
    if (message === null) return undefined
    schedule()
    return clear
  }, [clear, message, schedule])

  const tones: Record<ToastMessage['tone'], string> = {
    neutral: 'bg-ink text-ground',
    good: 'bg-ink text-ground border-l-4 border-good',
    over: 'bg-ink text-ground border-l-4 border-over',
  }

  return (
    <ToastContext.Provider value={api}>
      {children}
      {message === null || overlayRoot === null
        ? null
        : createPortal(
            <div className="pointer-events-none absolute inset-x-4 bottom-4 flex justify-center">
              <div
                key={message.id}
                role="status"
                aria-live="polite"
                onPointerDown={clear}
                onPointerUp={schedule}
                onPointerCancel={schedule}
                className={`pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-card px-4 py-3 text-sm ${tones[message.tone]}`}
              >
                <span className="min-w-0 flex-1">{message.text}</span>
                <button
                  type="button"
                  onClick={() => setMessage(null)}
                  aria-label={t('action.close')}
                  className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-chip focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ground"
                >
                  <X aria-hidden size={18} />
                </button>
              </div>
            </div>,
            overlayRoot,
          )}
    </ToastContext.Provider>
  )
}

/**
 * The toast API.
 *
 * Throws when there is no provider, rather than returning a no-op: a toast that
 * silently does not appear is a bug that reaches production, and this is a
 * developer-time mistake with a developer-time fix.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (api === null) throw new Error('useToast must be used inside <ToastProvider>')
  return api
}
